// src/pages/AdminFiscalDevices.jsx
import React, { useEffect, useMemo, useState } from "react";

/** Auto API base… */
const API =
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
    ? "http://localhost:5000"
    : (process.env.REACT_APP_API_BASE || "");

/* ------------------------------- helpers ------------------------------- */
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const colorStatus = (online) =>
  online === true ? "bg-emerald-500 text-black" :
  online === false ? "bg-rose-500 text-white" :
  "bg-slate-500 text-white";

const BTN     = "px-3 py-2 rounded-xl bg-gradient-to-br from-[#6c56b5] to-[#2c204f] text-white border border-white/20 hover:opacity-95";
const BTN_LITE= "px-3 py-2 rounded-xl bg-white/10 border border-white/20 hover:bg-white/15 text-white";
const BTN_OK  = "px-3 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white border border-white/10";
const BTN_WARN= "px-3 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-black";
const BTN_DNG = "px-3 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white";

function pill(cls, text) {
  return <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${cls}`}>{text}</span>;
}

/** JSON fetch helper (with credentials) */
async function j(url, opts) {
  const full = url.startsWith("http") ? url : `${API}${url}`;
  const r = await fetch(full, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...opts,
  });
  const ct = r.headers.get("content-type") || "";
  const data = ct.includes("application/json") ? await r.json() : null;
  if (!r.ok || data?.ok === false) {
    const msg = data?.error || data?.message || data?.details || data?.sqlMessage || `HTTP ${r.status} ${url}`;
    throw new Error(msg);
  }
  return data;
}

/** Try to load cinemas from either endpoint, normalize to {cinema_id, name, city} */
async function loadCinemas() {
  const candidates = ["/api/fiscal-devices/cinemas", "/api/cinemas"];
  for (const path of candidates) {
    try {
      const res = await j(path);
      const arr = Array.isArray(res) ? res : (res?.data || res?.rows || res || []);
      if (!Array.isArray(arr)) continue;
      const normalized = arr
        .map((c) => ({
          cinema_id: String(c.cinema_id ?? c.id ?? ""),
          name: c.cinema_name ?? c.name ?? "",
          city: c.city ?? c.town ?? "",
        }))
        .filter((c) => c.cinema_id && c.name);
      if (normalized.length) return normalized;
    } catch {}
  }
  return [];
}

function escapeHtml(s){
  return (s||"").replace(/[&<>"']/g,(m)=>({ "&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;" }[m]));
}
function fmtTs(ts) {
  if (!ts) return "—";
  const d = new Date(Number(ts) || ts);
  if (isNaN(d.getTime())) return String(ts);
  return d.toLocaleString();
}
/** filter live to only those not already registered (by bridge_key) */
function liveFiltered(live, devices){
  const keys = new Set((devices||[]).map(d => String(d.bridge_key||"")));
  return (live||[]).filter(x => !keys.has(String(x.bridge_key||"")));
}

/* -------------------------------- component -------------------------------- */
export default function AdminFiscalDevices() {
  // data
  const [cinemas, setCinemas] = useState([]);
  const [selectedCinema, setSelectedCinema] = useState("");
  const [devices, setDevices] = useState([]);
  const [live, setLive] = useState([]);
  const [loading, setLoading] = useState(false);

  // ui
  const [toast, setToast] = useState("");
  const [search, setSearch] = useState("");
  const [sortOnlineFirst, setSortOnlineFirst] = useState(true);

  // form/edit + probe
  const [editing, setEditing] = useState(null);
  const defaults = {
    cinema_id: "", name: "", model: "Daisy FX-1300", serial_no: "",
    erp_base_url: "http://127.0.0.1:7000", api_key: "", bridge_key: "",
    erp_pwd: "", com_port: "", baudrate: 115200, use_for: "both",
    is_active: 1, is_default: 0,
    erp_status_path: "/api", erp_info_path: "/api", erp_headers_path: "/api",
    erp_payments_path: "/api", erp_taxes_path: "/api", erp_operators_path: "/api",
    erp_x_path: "/document/x-report", erp_z_path: "/document/z-report", erp_print_path: "/document/receipt",
    operator_no: "", operator_pwd: "", default_dept_code: "",
  };
  const [form, setForm] = useState(defaults);
  const [probe, setProbe] = useState(null);
  const [probing, setProbing] = useState(false);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // auto-refresh
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshMs, setRefreshMs] = useState(10000);

  // attach modal
  const [attachOpen, setAttachOpen] = useState(false);
  const [attachRow, setAttachRow] = useState(null);
  const [attachCinema, setAttachCinema] = useState("");
  const [attachName, setAttachName] = useState("");
  const [attachUseFor, setAttachUseFor] = useState("both");
  const [attachMsg, setAttachMsg] = useState("");

  // snapshots modal
  const [snapOpen, setSnapOpen] = useState(false);
  const [snapDevice, setSnapDevice] = useState(null); // whole row
  const [snapTab, setSnapTab] = useState("info");     // current tab

  const shortToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 2500); };

  useEffect(() => { (async () => { try { setCinemas(await loadCinemas()); } catch(e) { console.error(e); } })(); }, []);
  useEffect(() => { if (selectedCinema) setForm((f) => ({ ...f, cinema_id: String(selectedCinema) })); }, [selectedCinema]);

  const loadDevices = async (cinema_id) => {
    setLoading(true);
    try {
      const q = cinema_id ? `?cinema_id=${cinema_id}` : "";
      const data = await j(`/api/fiscal-devices/devices-with-status${q}`);
      setDevices(data?.rows || []);
    } finally { setLoading(false); }
  };
  const loadLive = async () => {
    try {
      const data = await j(`/api/fiscal-devices/live`);
      setLive(data?.rows || []);
    } catch { setLive([]); }
  };
  useEffect(() => { loadDevices(selectedCinema); loadLive(); }, [selectedCinema]);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => { loadDevices(selectedCinema); loadLive(); }, refreshMs);
    return () => clearInterval(id);
  }, [autoRefresh, refreshMs, selectedCinema]);

  /* -------------------------- actions / calls -------------------------- */
  const trimOrNull = (v) => (typeof v === "string" ? (v.trim() || null) : (v ?? null));

  const doProbe = async () => {
    if (!form.erp_base_url && !form.bridge_key) return shortToast("❗ Попълнете ERP URL или Bridge key.");
    if (!form.com_port && !form.bridge_key) return shortToast("❗ COM порт е задължителен, ако не ползвате Bridge.");

    setProbing(true); setProbe(null);
    try {
      const { data } = await j(`/api/fiscal-devices/probe`, {
        method: "POST",
        body: JSON.stringify({
          erp_base_url: trimOrNull(form.erp_base_url),
          bridge_key: trimOrNull(form.bridge_key),
          com_port: trimOrNull(form.com_port),
          erp_pwd: trimOrNull(form.erp_pwd) || "",
        }),
      });
      setProbe(data || {});
      const ser = data?.detected_serial || "";
      if (ser) setF("serial_no", ser);
      if (!form.name && ser) setF("name", `Daisy ${ser}`);
      shortToast("✅ Прочетено от устройството");
    } catch (e) { console.error(e); shortToast("❌ Неуспешно четене"); }
    finally { setProbing(false); }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        cinema_id: Number(form.cinema_id || selectedCinema),
        name: (form.name || "").trim(),
        model: (form.model || "").trim(),
        serial_no: (form.serial_no || "").trim() || null,
        erp_base_url: trimOrNull(form.erp_base_url),
        api_key: trimOrNull(form.api_key),
        bridge_key: trimOrNull(form.bridge_key),
        erp_pwd: trimOrNull(form.erp_pwd),
        com_port: trimOrNull(form.com_port),
        baudrate: Number(form.baudrate) || 115200,
        use_for: form.use_for || "both",
        is_active: Number(form.is_active) || 0,
        is_default: Number(form.is_default) || 0,
        erp_status_path: (form.erp_status_path || "/api").trim(),
        erp_info_path: (form.erp_info_path || "/api").trim(),
        erp_headers_path: (form.erp_headers_path || "/api").trim(),
        erp_payments_path: (form.erp_payments_path || "/api").trim(),
        erp_taxes_path: (form.erp_taxes_path || "/api").trim(),
        erp_operators_path: (form.erp_operators_path || "/api").trim(),
        erp_x_path: (form.erp_x_path || "/document/x-report").trim(),
        erp_z_path: (form.erp_z_path || "/document/z-report").trim(),
        erp_print_path: (form.erp_print_path || "/document/receipt").trim(),
        operator_no: form.operator_no ? Number(form.operator_no) : null,
        operator_pwd: trimOrNull(form.operator_pwd),
        default_dept_code: form.default_dept_code ? Number(form.default_dept_code) : null,
      };

      if (!payload.cinema_id) return shortToast("❗ Моля изберете кино");
      if (!payload.name) return shortToast("❗ Въведете име");
      if (!payload.erp_base_url && !payload.bridge_key) return shortToast("❗ ERP URL или Bridge key");
      if (!payload.com_port && !payload.bridge_key) return shortToast("❗ COM порт е задължителен, ако няма Bridge");

      if (editing?.device_id) {
        await j(`/api/fiscal-devices/devices/${editing.device_id}`, { method: "PUT", body: JSON.stringify(payload) });
        shortToast("✅ Обновено");
      } else {
        await j(`/api/fiscal-devices/devices`, { method: "POST", body: JSON.stringify(payload) });
        shortToast("✅ Добавено");
      }

      setEditing(null); setForm(defaults);
      const cid = payload.cinema_id;
      setSelectedCinema(String(cid));
      await loadDevices(cid);
    } catch (e) { console.error(e); shortToast("❌ Грешка: " + (e.message || "")); }
  };

  const readWhat = async (d, what) => {
    const r = await j(`/api/fiscal-devices/devices/${d.device_id}/read`, {
      method: "POST", body: JSON.stringify({ what }),
    });
    // update in-memory snapshots so the modal refreshes immediately
    setDevices(prev => prev.map(x => x.device_id === d.device_id
      ? { ...x,
          ...(what==="info"        ? { snapshot_info: r?.data } : {}),
          ...(what==="headers"     ? { snapshot_headers: r?.data } : {}),
          ...(what==="taxes"       ? { snapshot_taxes: r?.data } : {}),
          ...(what==="payments"    ? { snapshot_payments: r?.data } : {}),
          ...(what==="departments" ? { snapshot_departments: r?.data } : {}),
          ...(what==="operators"   ? { snapshot_operators: r?.data } : {}),
        }
      : x));
    shortToast("✅ Обновено от устройство");
  };

  const ping = async (d) => { await j(`/api/fiscal-devices/devices/${d.device_id}/ping`, { method: "POST" }); await loadDevices(selectedCinema); };
  const pingAll = async () => {
    for (const d of devices) { try { await j(`/api/fiscal-devices/devices/${d.device_id}/ping`, { method: "POST" }); } catch {} }
    await loadDevices(selectedCinema);
  };
  const setActive = async (d, active) => {
    await j(`/api/fiscal-devices/devices/${d.device_id}`, {
      method: "PUT", body: JSON.stringify({ is_active: active ? 1 : 0 }),
    });
    await loadDevices(selectedCinema);
  };
  const rotateKey = async (d) => {
    const r = await j(`/api/fiscal-devices/devices/${d.device_id}/rotate-bridge-key`, { method: "POST" });
    shortToast(`🔑 Нов ключ: ${r.bridge_key}`); await loadDevices(selectedCinema);
  };
  const removeRow = async (d) => {
    if (!window.confirm(`Да изтрия ли устройството "${d.name}"?`)) return;
    await j(`/api/fiscal-devices/devices/${d.device_id}`, { method: "DELETE" });
    shortToast("🗑️ Изтрито"); await loadDevices(selectedCinema);
  };
  const xReport = async () => {
    if (!selectedCinema) return shortToast("❗ Изберете кино");
    try { await j(`/api/fiscal/x-report`, { method: "POST", body: JSON.stringify({ cinema_id: Number(selectedCinema) }) }); shortToast("🧾 X отчет изпратен"); }
    catch(e){ shortToast("❌ Неуспешно: "+(e.message||"")); }
  };
  const zReport = async () => {
    if (!selectedCinema) return shortToast("❗ Изберете кино");
    try { await j(`/api/fiscal/z-report`, { method: "POST", body: JSON.stringify({ cinema_id: Number(selectedCinema) }) }); shortToast("🧾 Z отчет изпратен"); }
    catch(e){ shortToast("❌ Неуспешно: "+(e.message||"")); }
  };
  const exportJson = () => {
    const blob = new Blob([JSON.stringify(devices, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "fiscal_devices.json"; a.click();
    URL.revokeObjectURL(url);
  };

  const editRow = (d) => {
    setEditing(d);
    setForm({
      cinema_id: String(d.cinema_id || ""),
      name: d.name || "",
      model: d.model || "Daisy FX-1300",
      serial_no: d.serial_no || "",
      erp_base_url: d.erp_base_url || "",
      api_key: d.api_key || "",
      erp_pwd: d.erp_pwd || "",
      com_port: d.com_port || "",
      baudrate: d.baudrate || 115200,
      use_for: d.use_for || "both",
      is_active: d.is_active ? 1 : 0,
      erp_status_path: d.erp_status_path || "/api",
      erp_info_path: d.erp_info_path || "/api",
      erp_headers_path: d.erp_headers_path || "/api",
      erp_payments_path: d.erp_payments_path || "/api",
      erp_taxes_path: d.erp_taxes_path || "/api",
      erp_operators_path: d.erp_operators_path || "/api",
      operator_no: d.operator_no || "",
      operator_pwd: d.operator_pwd || "",
      default_dept_code: d.default_dept_code || "",
      bridge_key: d.bridge_key || "",
      is_default: d.is_default ? 1 : 0,
      erp_x_path: d.erp_x_path || "/document/x-report",
      erp_z_path: d.erp_z_path || "/document/z-report",
      erp_print_path: d.erp_print_path || "/document/receipt",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };



  // live attach modal helpers
const openAttach = (row) => {
  setAttachRow(row);
  setAttachCinema(selectedCinema || "");
  setAttachName(`Daisy ${row.serial_no || row.bridge_key}`);
  setAttachUseFor("both");
  setAttachMsg("");
  setAttachOpen(true);
};

const closeAttach = () => {
  setAttachOpen(false);
  setAttachRow(null);
  setAttachMsg("");
};

const doAttach = async () => {
  if (!attachRow) return;
  if (!attachCinema) return shortToast("❗ Изберете кино");
  try {
    const r = await j(`/api/fiscal-devices/attach`, {
      method: "POST",
      body: JSON.stringify({
        bridge_key: attachRow.bridge_key,
        cinema_id: Number(attachCinema),
        name: attachName.trim(),
        use_for: attachUseFor,
        is_active: 1,
        is_default: 0,
      }),
    });
    setAttachMsg(
      r.created_id
        ? `Създадено #${r.created_id}`
        : r.updated_id
        ? `Обновено #${r.updated_id}`
        : "OK"
    );
    setTimeout(async () => {
      closeAttach();
      await loadDevices(selectedCinema);
      await loadLive();
    }, 600);
  } catch (e) {
    setAttachMsg("Грешка: " + String(e?.message || e));
  }
};

  // snapshots modal
  const openSnapshots = (row) => { setSnapDevice(row); setSnapTab("info"); setSnapOpen(true); };
  const closeSnapshots = () => { setSnapOpen(false); setSnapDevice(null); };

  // derived
  const cinemasById = useMemo(
    () => Object.fromEntries(cinemas.map((c) => [String(c.cinema_id), c])),
    [cinemas]
  );
  const filteredDevices = useMemo(() => {
    const q = search.trim().toLowerCase();
    let arr = devices;
    if (q) {
      arr = arr.filter(d =>
        (d.name||"").toLowerCase().includes(q) ||
        (d.serial_no||"").toLowerCase().includes(q) ||
        (d.com_port||"").toLowerCase().includes(q) ||
        (d.model||"").toLowerCase().includes(q)
      );
    }
    if (sortOnlineFirst) {
      arr = [...arr].sort((a,b) => (Number(b.online||0)-Number(a.online||0)) || String(a.name).localeCompare(String(b.name)));
    }
    return arr;
  }, [devices, search, sortOnlineFirst]);

  /* ---------------------------------- UI ---------------------------------- */
  return (
    <div className="min-h-screen relative p-4 md:p-6 [color-scheme:light]">
      {/* background */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <img
          src="https://latonacinema.com/wp-content/uploads/2025/06/cinema-hall-with-rows-of-grey-seats-in-darkness.jpg"
          alt="фон"
          className="h-full w-full object-cover blur-[2px] scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#2c204f]/80 via-black/60 to-[#6c56b5]/70" />
      </div>

      {/* card container */}
      <div className="max-w-screen-2xl mx-auto bg-white/8 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl p-4 md:p-6 text-white">
        {/* header */}
        <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
          <div className="flex items-center gap-3">
            {/* ← Back */}
            <button
              className="mr-2 rounded-xl bg-white/10 border border-white/20 px-3 py-2 hover:bg-white/15"
              onClick={() => (window.history.length > 1 ? window.history.back() : (window.location.href = "/admin"))}
              title="Назад"
            >
              ← Back
            </button>
            <img
              src="https://latonacinema.com/wp-content/uploads/2025/06/cropped-latonacinema.png"
              alt="logo"
              className="h-10"
            />
            <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight">🖨️ Фискални устройства</h1>
          </div>
          {toast && <span className="text-sm bg-black/70 rounded-lg px-2 py-1">{toast}</span>}
        </div>

        {/* toolbar */}
        <div className="flex flex-wrap items-center gap-2 mb-5">
          <select
            value={selectedCinema}
            onChange={(e) => setSelectedCinema(e.target.value)}
            className="px-3 py-2 bg-white/90 border border-white/40 text-slate-900 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-400"
          >
            <option value="">Всички кина</option>
            {cinemas.map((c) => (
              <option key={c.cinema_id} value={String(c.cinema_id)}>
                {c.name}{c.city ? ` (${c.city})` : ""}
              </option>
            ))}
          </select>

          <input
            className="px-3 py-2 bg-white/90 border border-white/40 text-slate-900 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-400 w-56"
            placeholder="Търси: име, сериен, COM…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <label className="flex items-center gap-2 text-sm ml-1">
            <input type="checkbox" checked={sortOnlineFirst} onChange={(e)=>setSortOnlineFirst(e.target.checked)} />
            Онлайн първо
          </label>

          <button className={BTN_LITE} onClick={() => { loadDevices(selectedCinema); loadLive(); }} title="Ръчно обновяване">⟳ Обнови</button>
          <button className={BTN_LITE} onClick={pingAll} title="Ping всички">📡 Ping all</button>
          <button className={BTN_LITE} onClick={exportJson} title="Експорт JSON">⬇️ Export</button>

          <div className="ml-auto flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={autoRefresh} onChange={(e)=>setAutoRefresh(e.target.checked)} />
              Авто (сек)
            </label>
            <input
              type="range" min={5} max={60}
              value={Math.round(refreshMs/1000)}
              onChange={(e)=>setRefreshMs(clamp(Number(e.target.value)||10,5,60)*1000)}
              className="w-28"
            />
            <span className="w-8 text-right text-sm opacity-80">{Math.round(refreshMs/1000)}</span>

            <button className={BTN_WARN} onClick={xReport} title="X отчет (по кино)">X Report</button>
            <button className={BTN} onClick={zReport} title="Z отчет (по кино)">Z Report</button>
          </div>
        </div>

        {/* LIVE (unassigned) */}
        <div className="rounded-2xl border border-white/15 bg-white/5 p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-semibold">На живо (неприкачени)</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/10">
                <tr>
                  <Th>Online</Th>
                  <Th>Bridge</Th>
                  <Th>Serial</Th>
                  <Th>Model</Th>
                  <Th>COM</Th>
                  <Th>Baud</Th>
                  <Th>Host</Th>
                  <Th>Seen</Th>
                  <Th>Действия</Th>
                </tr>
              </thead>
              <tbody>
                {liveFiltered(live, devices).map((x) => (
                  <tr key={x.bridge_key} className="even:bg-white/5">
                    <Td>{pill("bg-emerald-500 text-black", "Online")}</Td>
                    <Td mono>{x.bridge_key}</Td>
                    <Td>{x.serial_no || "-"}</Td>
                    <Td>{x.model || "-"}</Td>
                    <Td>{x.com_port || "-"}</Td>
                    <Td>{x.baudrate || "-"}</Td>
                    <Td>{x.host || "-"}</Td>
                    <Td>{fmtTs(x.last_seen)}</Td>
                    <Td>
                      <div className="flex flex-wrap gap-2">
                        <button className={BTN_OK} onClick={() => openAttach(x)}>Прикачи…</button>
                      </div>
                    </Td>
                  </tr>
                ))}
                {!liveFiltered(live, devices).length && (
                  <tr><td className="p-3 text-center text-white/70" colSpan={9}>Няма неприкачени онлайн устройства.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Registered devices */}
        <div className="rounded-2xl border border-white/15 bg-white/5 p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-semibold">Регистрирани</h2>
            {loading && <span className="text-sm opacity-80">Зареждане…</span>}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/10">
                <tr>
                  <Th>Кино</Th>
                  <Th>Име</Th>
                  <Th>Модел</Th>
                  <Th>Сериен №</Th>
                  <Th>База</Th>
                  <Th>За</Th>
                  <Th>Статус</Th>
                  <Th>COM(DB/Live)</Th>
                  <Th>Baud(DB/Live)</Th>
                  <Th>Host</Th>
                  <Th>Версия</Th>
                  <Th>Действия</Th>
                </tr>
              </thead>
              <tbody>
                {filteredDevices.map((d) => (
                  <tr key={d.device_id} className="even:bg-white/5">
                    <Td>{cinemasById[String(d.cinema_id)]?.name || d.cinema_id}</Td>
                    <Td>{d.name}</Td>
                    <Td>{d.model || "-"}</Td>
                    <Td mono>{d.serial_no || "-"}</Td>
                    <Td title={d.effective_base || d.erp_base_url} className="max-w-[280px] truncate">{d.effective_base || d.erp_base_url}</Td>
                    <Td>{d.use_for === "tickets" ? "Билети" : d.use_for === "bar" ? "Бар" : "Билети+Бар"}</Td>
                    <Td>
                      {pill(colorStatus(d.online), d.online ? "WS Online" : (d.last_seen ? "Offline" : "—"))}
                      {d.last_error && <div className="text-[11px] opacity-75 mt-1" title={d.last_error}>err</div>}
                    </Td>
                    <Td>{(d.com_port || "")}{d.com_port_live ? (" / " + d.com_port_live) : ""}</Td>
                    <Td>{(d.baudrate || "")}{d.baudrate_live ? (" / " + d.baudrate_live) : ""}</Td>
                    <Td>{d.host_live || ""}</Td>
                    <Td>{d.version_live || ""}</Td>
                    <Td>
                      <div className="flex flex-wrap gap-2">
                        <button className={BTN_LITE} onClick={() => ping(d)}>Ping</button>
                        <button className={BTN_LITE} onClick={() => readWhat(d, "info")}>Info</button>
                        <button className={BTN_LITE} onClick={() => readWhat(d, "headers")}>Клише</button>
                        <button className={BTN_LITE} onClick={() => readWhat(d, "taxes")}>Данъчни</button>
                        <button className={BTN_LITE} onClick={() => readWhat(d, "payments")}>Плащания</button>
                        <button className={BTN_LITE} onClick={() => readWhat(d, "departments")}>Департ.</button>
                        <button className={BTN_LITE}
                          onClick={() => { navigator.clipboard.writeText(`SERVER_WSS=wss://<host>/ws/bridge BRIDGE_KEY=${d.active_bridge_key||d.bridge_key||""} LOCAL_BASE=${d.erp_base_url||""} node local-bridge/index.js`); shortToast("📋 Копирано"); }}>
                          Copy bridge cmd
                        </button>
                        <button className={BTN_LITE} onClick={() => rotateKey(d)}>Rotate key</button>
                        <button className={BTN_LITE} onClick={() => editRow(d)}>Edit</button>
                        <button className={BTN_OK}  onClick={() => openSnapshots(d)}>👁 Snapshots</button>
                        <button className={BTN_DNG} onClick={() => removeRow(d)}>Delete</button>
                        <button className={BTN_LITE}
                          onClick={() => setActive(d, d.is_active ? 0 : 1)}
                          title={d.is_active ? "Деактивирай" : "Активирай"}>
                          {d.is_active ? "🔕" : "🔔"}
                        </button>
                      </div>
                    </Td>
                  </tr>
                ))}
                {!filteredDevices.length && (
                  <tr><td className="p-3 text-center text-white/70" colSpan={12}>Няма устройства.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Add / Edit form */}
        <div className="rounded-2xl border border-white/15 bg-white/5 p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-semibold">{editing ? "✏️ Редакция" : "➕ Ново устройство"}</h2>
            {editing && (
              <button className="text-sm underline decoration-white/60 hover:decoration-white" onClick={() => { setEditing(null); setForm(defaults); setProbe(null); }}>
                Изчисти
              </button>
            )}
          </div>

          <form className="grid grid-cols-1 md:grid-cols-2 gap-3" onSubmit={onSubmit}>
            <Select label="Кино" val={form.cinema_id || ""} on={(v)=>setF("cinema_id", v)} opts={[
              ["", "— изберете —"],
              ...cinemas.map(c => [String(c.cinema_id), `${c.name}${c.city ? ` (${c.city})` : ""}`])
            ]} />

            <Field label="Име" val={form.name} on={(v) => setF("name", v)} />
            <Field label="Модел" val={form.model} on={(v) => setF("model", v)} />
            <Field label="Сериен № (DY…)" val={form.serial_no} on={(v) => setF("serial_no", v)} />

            <Field label="ERP базов URL" val={form.erp_base_url} on={(v) => setF("erp_base_url", v)} />
            <Field label="COM порт (напр. COM3)" val={form.com_port} on={(v) => setF("com_port", v)} />
            <Field label="ERP парола (CmdPWD, по избор)" val={form.erp_pwd} on={(v) => setF("erp_pwd", v)} />
            <Field label="API Key (локален мост, по избор)" val={form.api_key} on={(v)=>setF("api_key", v)} />

            <Select label="Ползване за" val={form.use_for} on={(v)=>setF("use_for", v)} opts={[
              ["both","Билети + Бар"],["tickets","Само билети"],["bar","Само бар"]
            ]} />
            <Select label="Активно" val={String(form.is_active)} on={(v)=>setF("is_active", Number(v))} opts={[["1","Да"],["0","Не"]]} />

            <Field label="Bridge key (WS)" val={form.bridge_key} on={(v)=>setF("bridge_key", v)} />
            <Select label="По подразбиране (default)" val={String(form.is_default)} on={(v)=>setF("is_default", Number(v))} opts={[["0","Не"],["1","Да"]]} />

            {/* Paths */}
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-3 border-t border-white/10 pt-3">
              <Field label="Път: Status"   val={form.erp_status_path}   on={(v)=>setF("erp_status_path", v)} />
              <Field label="Път: Info"     val={form.erp_info_path}     on={(v)=>setF("erp_info_path", v)} />
              <Field label="Път: Headers"  val={form.erp_headers_path}  on={(v)=>setF("erp_headers_path", v)} />
              <Field label="Път: Payments" val={form.erp_payments_path} on={(v)=>setF("erp_payments_path", v)} />
              <Field label="Път: Taxes"    val={form.erp_taxes_path}    on={(v)=>setF("erp_taxes_path", v)} />
              <Field label="Път: Operators"val={form.erp_operators_path}on={(v)=>setF("erp_operators_path", v)} />
              <Field label="Път: X-отчет"  val={form.erp_x_path}        on={(v)=>setF("erp_x_path", v)} />
              <Field label="Път: Z-отчет"  val={form.erp_z_path}        on={(v)=>setF("erp_z_path", v)} />
              <Field label="Път: Печат"    val={form.erp_print_path}    on={(v)=>setF("erp_print_path", v)} />
            </div>

            {/* Defaults for POS */}
            <Field label="Оператор № (по подразбиране)" val={form.operator_no} on={(v)=>setF("operator_no", v)} />
            <Field label="Парола оператор" val={form.operator_pwd} on={(v)=>setF("operator_pwd", v)} />
            <Field label="Департамент (данъчна група) по подразбиране" val={form.default_dept_code} on={(v)=>setF("default_dept_code", v)} />

            <div className="md:col-span-2 flex gap-2 justify-end mt-2">
              <button
                type="button"
                onClick={doProbe}
                disabled={probing || !(form.erp_base_url || form.bridge_key) || (!form.com_port && !form.bridge_key)}
                className={BTN_LITE}
              >
                {probing ? "Четене…" : "🔍 Прочети от устройството"}
              </button>
              <button type="submit" className={BTN}>
                {editing ? "💾 Запази промените" : "➕ Добави устройство"}
              </button>
            </div>
          </form>

          {probe && (
            <div className="mt-3 text-sm bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="font-semibold mb-1">Резултат от четене</div>
              <pre className="text-xs overflow-auto max-h-64">{JSON.stringify(probe, null, 2)}</pre>
            </div>
          )}
        </div>

        {/* Attach LIVE modal */}
        {attachOpen && (
          <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeAttach} />
            <div className="relative bg-white/90 text-slate-900 rounded-2xl shadow-2xl p-5 w-[92vw] max-w-md border border-white">
              <h3 className="text-lg font-semibold mb-3">Прикачи устройство</h3>
              <p className="text-sm text-slate-600 mb-2">
                Bridge: <b>{attachRow?.bridge_key}</b> · Serial: <b>{attachRow?.serial_no || "-"}</b>
              </p>

              <label className="block text-sm mb-1">Кино</label>
              <select className="w-full border rounded-md p-2 mb-2" value={attachCinema} onChange={(e)=>setAttachCinema(e.target.value)}>
                <option value="">— изберете —</option>
                {cinemas.map((c)=>(
                  <option key={c.cinema_id} value={c.cinema_id}>
                    {c.name}{c.city ? ` (${c.city})` : ""}
                  </option>
                ))}
              </select>

              <FieldL label="Име на устройство" val={attachName} on={setAttachName} />
              <div className="mt-2">
                <label className="block text-sm mb-1">Ползване за</label>
                <select className="w-full border rounded-md p-2" value={attachUseFor} onChange={(e)=>setAttachUseFor(e.target.value)}>
                  <option value="both">Билети + Бар</option>
                  <option value="tickets">Само билети</option>
                  <option value="bar">Само бар</option>
                </select>
              </div>

              <div className="flex gap-2 justify-end mt-4">
                <button className="px-3 py-2 rounded border" onClick={closeAttach}>Отказ</button>
                <button className="px-3 py-2 rounded bg-black text-white disabled:opacity-60" onClick={doAttach} disabled={!attachCinema}>
                  Прикачи
                </button>
              </div>

              {attachMsg && <div className="text-sm mt-3">{attachMsg}</div>}
            </div>
          </div>
        )}

        {/* Snapshots modal */}
        {snapOpen && snapDevice && (
          <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeSnapshots} />
            <div className="relative bg-white/95 text-slate-900 rounded-2xl shadow-2xl p-5 w-[96vw] max-w-4xl border border-white">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold">
                  👁 Snapshots — <span className="font-normal">{snapDevice.name}</span>
                </h3>
                <button onClick={closeSnapshots} className="px-3 py-1 rounded bg-slate-800 text-white">✕</button>
              </div>

              {/* tabs */}
              <div className="flex gap-2 mb-3 flex-wrap">
                {["info","headers","taxes","payments","operators","departments"].map(t => (
                  <button
                    key={t}
                    onClick={()=>setSnapTab(t)}
                    className={`px-3 py-1 rounded-full border ${snapTab===t ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300"}`}
                  >
                    {t}
                  </button>
                ))}
                <div className="ml-auto flex gap-2">
                  <button className="px-3 py-1 rounded bg-black text-white" onClick={()=>readWhat(snapDevice, snapTab)}>Refresh {snapTab}</button>
                </div>
              </div>

              {/* viewer */}
              <div className="rounded border border-slate-200 bg-white">
                <pre className="p-3 text-xs overflow-auto max-h-[60vh]">
                  {JSON.stringify(
                    snapTab==="info"        ? (snapDevice.snapshot_info ?? null)
                    : snapTab==="headers"    ? (snapDevice.snapshot_headers ?? null)
                    : snapTab==="taxes"      ? (snapDevice.snapshot_taxes ?? null)
                    : snapTab==="payments"   ? (snapDevice.snapshot_payments ?? null)
                    : snapTab==="operators"  ? (snapDevice.snapshot_operators ?? null)
                    : /* departments */         (snapDevice.snapshot_departments ?? null),
                    null, 2
                  )}
                </pre>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* -------------------- small UI bits (dark theme) -------------------- */
function Field({ label, val, on, placeholder }) {
  return (
    <div>
      <label className="block text-sm mb-1">{label}</label>
      <input
        className="px-3 py-2 bg-white/90 border border-white/40 text-slate-900 rounded-xl w-full focus:outline-none focus:ring-2 focus:ring-purple-400"
        value={val || ""}
        onChange={(e) => on(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}
function FieldL({ label, val, on, placeholder }) {
  return (
    <div>
      <label className="block text-sm mb-1">{label}</label>
      <input
        className="px-3 py-2 bg-white border border-slate-300 text-slate-900 rounded-md w-full"
        value={val || ""}
        onChange={(e) => on(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}
function Select({ label, val, on, opts }) {
  return (
    <div>
      <label className="block text-sm mb-1">{label}</label>
      <select
        className="px-3 py-2 bg-white/90 border border-white/40 text-slate-900 rounded-xl w-full focus:outline-none focus:ring-2 focus:ring-purple-400"
        value={val} onChange={(e)=>on(e.target.value)}
      >
        {opts.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
      </select>
    </div>
  );
}
const Th = ({ children }) => (
  <th className="p-2 border border-white/10 text-left font-semibold text-white/90">{children}</th>
);
const Td = ({ children, className, mono }) => (
  <td className={`p-2 border border-white/10 ${className || ""} ${mono ? "font-mono" : ""}`}>{children}</td>
);
